import { useMemo, useState } from "react";
import Button from "@/components/base/Button";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import EmptyState from "@/pages/admin/components/EmptyState";
import EstadoBadge from "@/pages/admin/components/EstadoBadge";
import FiltrosSolicitudes from "./components/FiltrosSolicitudes";
import SolicitudDetalle from "./components/SolicitudDetalle";
import { useSolicitudes } from "@/hooks/useSolicitudes";
import { supabase } from "@/lib/supabase";
import { formatFecha } from "@/lib/format";
import { tipoServicioOptions } from "@/lib/solicitudOptions";
import type { Solicitud } from "@/types";

export default function AdminSolicitudes() {
  const { solicitudes, loading, error, reload, setSolicitudes } = useSolicitudes();
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [servicio, setServicio] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  const servicios = useMemo(() => {
    const presentes = new Set(solicitudes.map((s) => s.tipo_servicio).filter(Boolean));
    const lista = Array.from(presentes).sort();
    return lista.length > 0 ? lista : tipoServicioOptions;
  }, [solicitudes]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return solicitudes.filter((solicitud) => {
      if (estado && solicitud.estado !== estado) return false;
      if (servicio && solicitud.tipo_servicio !== servicio) return false;
      if (!term) return true;
      return [
        solicitud.numero,
        solicitud.nombre_cliente,
        solicitud.email,
        solicitud.telefono,
        solicitud.empresa,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [solicitudes, q, estado, servicio]);

  const selected = useMemo(
    () => solicitudes.find((solicitud) => solicitud.id === selectedId) ?? null,
    [solicitudes, selectedId],
  );

  const clearFilters = () => {
    setQ("");
    setEstado("");
    setServicio("");
  };

  const handleEstadoChange = async (id: string, nuevoEstado: string) => {
    setUpdatingId(id);
    setActionError("");
    const previo = solicitudes;
    setSolicitudes((list) =>
      list.map((solicitud) =>
        solicitud.id === id ? { ...solicitud, estado: nuevoEstado } : solicitud,
      ),
    );
    try {
      const { error: updateError } = await supabase
        .from("solicitudes_de_servicio")
        .update({ estado: nuevoEstado, actualizado_en: new Date().toISOString() })
        .eq("id", id);
      if (updateError) throw updateError;
    } catch {
      setSolicitudes(previo);
      setActionError("No pudimos actualizar el estado. Revisa tu conexión e intenta nuevamente.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Solicitudes de servicio"
        description="Revisa, filtra y actualiza el estado de cada solicitud recibida desde el sitio público."
        action={
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={reload} disabled={loading}>
            Actualizar
          </Button>
        }
      />

      {actionError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {actionError}
        </div>
      )}

      {error ? (
        <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-wifi-off-line text-2xl leading-none" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
            No se pudieron cargar las solicitudes
          </h3>
          <p className="mt-1.5 max-w-md text-sm text-foreground-600">{error}</p>
          <Button variant="primary" size="md" icon="ri-refresh-line" onClick={reload} className="mt-5">
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <FiltrosSolicitudes
            q={q}
            estado={estado}
            servicio={servicio}
            servicios={servicios}
            resultCount={filtered.length}
            total={solicitudes.length}
            onQ={setQ}
            onEstado={setEstado}
            onServicio={setServicio}
            onClear={clearFilters}
          />

          <div className="mt-4">
            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2, 3].map((key) => (
                  <div key={key} className="h-16 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon="ri-file-search-line"
                title={solicitudes.length === 0 ? "Todavía no hay solicitudes" : "Sin resultados"}
                description={
                  solicitudes.length === 0
                    ? "Cuando un cliente complete el formulario de servicio, la solicitud aparecerá aquí."
                    : "No encontramos solicitudes con los filtros aplicados. Prueba con otros criterios."
                }
                action={
                  solicitudes.length > 0 ? (
                    <Button variant="outline" size="md" icon="ri-refresh-line" onClick={clearFilters}>
                      Limpiar filtros
                    </Button>
                  ) : null
                }
              />
            ) : (
              <>
                {/* Tabla (pantallas medianas y grandes) */}
                <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 md:block">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[880px] text-left">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100 text-xs font-label uppercase tracking-wide text-foreground-500">
                          <th className="px-4 py-3 font-semibold">Número</th>
                          <th className="px-4 py-3 font-semibold">Cliente</th>
                          <th className="px-4 py-3 font-semibold">Servicio</th>
                          <th className="px-4 py-3 font-semibold">Unidad</th>
                          <th className="px-4 py-3 font-semibold">Fecha</th>
                          <th className="px-4 py-3 font-semibold">Estado</th>
                          <th className="px-4 py-3 font-semibold">Registrada</th>
                          <th className="px-4 py-3 text-right font-semibold">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200">
                        {filtered.map((solicitud) => (
                          <tr key={solicitud.id} className="transition-colors hover:bg-background-100">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-label font-semibold text-primary-700">
                              {solicitud.numero}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-foreground-900">
                                {solicitud.nombre_cliente}
                              </p>
                              <p className="text-xs text-foreground-500">{solicitud.email}</p>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground-700">
                              {solicitud.tipo_servicio}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground-600">
                              {solicitud.unidad ?? "—"}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-600">
                              {solicitud.fecha ? formatFecha(solicitud.fecha) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <EstadoBadge estado={solicitud.estado} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-500">
                              {formatFecha(solicitud.creado_en)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => setSelectedId(solicitud.id)}
                                className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                              >
                                <i className="ri-eye-line text-base leading-none" aria-hidden="true"></i>
                                Ver
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tarjetas (móvil) */}
                <ul className="space-y-3 md:hidden">
                  {filtered.map((solicitud) => (
                    <li key={solicitud.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(solicitud.id)}
                        className="w-full cursor-pointer rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-300"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-label font-semibold text-primary-700">
                            {solicitud.numero}
                          </span>
                          <EstadoBadge estado={solicitud.estado} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-foreground-900">
                          {solicitud.nombre_cliente}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {solicitud.tipo_servicio} · {formatFecha(solicitud.creado_en)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}

      {selected && (
        <SolicitudDetalle
          solicitud={selected as Solicitud}
          updating={updatingId === selected.id}
          onClose={() => setSelectedId(null)}
          onEstadoChange={handleEstadoChange}
        />
      )}
    </div>
  );
}